// Live footage-analysis progress for a case: which pass is running, how far
// through it is, and an overall percentage. Polls the cheap progress
// endpoint while any file is still extracting or analyzing.
import { useEffect, useState } from "react";
import axios from "axios";
import cn from "classnames";
import { VideoProgressState } from "../../db/schema";

export type FootageProgressItem = {
  fileId: string;
  fileName: string | null;
  status: "pending" | "extracting" | "analyzing" | "completed" | "failed";
  mode?: "verify" | "enrich";
  error?: string;
  durationSec?: number;
  progress: VideoProgressState | null;
  eventCount: number | null;
};

export function useFootageProgress(caseId: string | undefined, active: boolean) {
  const [items, setItems] = useState<FootageProgressItem[]>([]);
  useEffect(() => {
    if (!caseId || !active) return;
    let cancelled = false;
    const load = () =>
      axios
        .get<FootageProgressItem[]>(`/api/video/cases/${caseId}/progress`)
        .then((r) => {
          if (!cancelled) setItems(r.data);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [caseId, active]);
  return items;
}

export function describeProgress(item: FootageProgressItem): { label: string; detail: string; pct: number | null } {
  const minutes = item.durationSec ? Math.round(item.durationSec / 60) : null;
  if (item.status === "failed") return { label: "Footage analysis failed", detail: item.error || "", pct: null };
  if (item.status === "completed") return { label: "Footage read", detail: item.eventCount != null ? `${item.eventCount} moment${item.eventCount === 1 ? "" : "s"} logged` : "", pct: 100 };
  if (item.status === "pending") return { label: "Waiting for the upload", detail: "", pct: 0 };
  if (item.status === "extracting") return { label: "Extracting stills from the footage", detail: minutes ? `${minutes} min of video` : "one still per second", pct: 3 };
  const p = item.progress;
  if (!p) return { label: "Reading the footage", detail: "starting the coverage pass", pct: 5 };
  if (p.stage === "triage") return { label: "Reading the footage", detail: `coverage pass ${Math.min(p.done + 1, p.total)} of ${p.total}: one look at every minute`, pct: p.pct };
  if (p.stage === "detail") return { label: "Reading the footage", detail: `frame by frame, ${Math.min(p.done + 1, p.total)} of ${p.total} flagged minute${p.total === 1 ? "" : "s"}`, pct: p.pct };
  if (p.stage === "summary") return { label: "Reading the footage", detail: "writing the summary", pct: p.pct };
  return { label: "Checking the draft against the footage", detail: "", pct: p.pct };
}

const FootageProgress: React.FC<{ caseId: string | undefined; active?: boolean; className?: string }> = ({ caseId, active = true, className }) => {
  const items = useFootageProgress(caseId, active);
  if (!items.length) return null;
  return (
    <div className={cn("bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900 rounded-lg p-4 text-left space-y-3", className)}>
      <p className="text-xs text-purple-700 dark:text-purple-300">
        <strong>Footage:</strong>
      </p>
      {items.map((it) => {
        const d = describeProgress(it);
        return (
          <div key={it.fileId}>
            <div className="flex items-baseline gap-2 text-xs">
              <span className="font-medium text-purple-800 dark:text-purple-200 truncate">{it.fileName || "video"}</span>
              {d.pct != null && <span className="ml-auto tabular-nums text-purple-700 dark:text-purple-300">{d.pct}%</span>}
            </div>
            <p className={cn("text-xs mt-0.5", it.status === "failed" ? "text-red-700 dark:text-red-400" : "text-purple-700 dark:text-purple-300")}>
              {d.label}
              {d.detail ? ` · ${d.detail}` : ""}
            </p>
            {d.pct != null && it.status !== "failed" && (
              <div className="mt-1.5 h-1.5 rounded-full bg-purple-200/70 dark:bg-purple-900/60 overflow-hidden">
                <div className="h-full rounded-full bg-purple-500 transition-[width] duration-700 ease-out" style={{ width: `${Math.max(2, d.pct)}%` }} />
              </div>
            )}
            {it.mode === "enrich" && it.status !== "completed" && it.status !== "failed" && (
              <p className="text-[11px] text-purple-700/80 dark:text-purple-300/80 mt-1">The narrative waits for this so it can include what the camera shows.</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FootageProgress;
